import useModal from '@carvana/ds-modal';
import Spinner from '@carvana/ds-spinner';
import React from 'react';
import styles from './plaid-modal.module.css';
import { useNativePlaid } from './use-native-plaid';
import { usePlaid } from './use-plaid';

type PlaidModalProps = ReturnType<typeof useModal> & {
  paymentAmount: number;
  onSuccess: () => void;
  onExit: () => void;
  onError: () => void;
};

const PlaidModal = ({ Modal, paymentAmount, onSuccess, onExit, onError }: PlaidModalProps) => {
  const { plaidStyleOverride } = usePlaid({
    paymentAmount,
    onSuccess,
    onExit,
    onError
  });

  return (
    <>
      <Modal
        className={styles.modal}
        closeIcon={false}
        divLine={false}
        desktopWidth="360px"
        mobileStyle="full"
        disableOutsideClose
        disableEscapeClose
        contentPadding={false}
      >
        <div className={styles.container}>
          <Spinner size={57} />
        </div>
      </Modal>

      {plaidStyleOverride}
    </>
  );
};

export const usePlaidModal = ({
  paymentAmount,
  onSuccess,
  onExit,
  onError
}: {
  paymentAmount: number;
  onSuccess: () => Promise<void>;
  onExit: () => void;
  onError: () => void;
}) => {
  const modal = useModal();

  const { isNativeApp, openNativePlaid } = useNativePlaid({ onSuccess, onExit, onError });

  const plaidModal = modal.isOpen ? (
    <PlaidModal
      {...modal}
      paymentAmount={paymentAmount}
      onSuccess={() => {
        onSuccess().finally(() => {
          modal.closeModal();
        });
      }}
      onExit={() => {
        modal.closeModal();
        onExit();
      }}
      onError={() => {
        modal.closeModal();
        onError();
      }}
    />
  ) : null;

  const openPlaidModal = isNativeApp ? openNativePlaid : modal.openModal;

  return { plaidModal, openPlaidModal };
};
